// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Resources } from '@shared/localizations/Resources';
import { AssetsItem } from '../cache/AssetsItem';
import { _AccountItemSelected, _AccountItemAll } from '../elements/Lookup/TerceraAccountLookup';
import { QuickTableEditingInfo } from '../elements/QuickTable/QuickTableMisc';
import { TerceraQuickTreeEvents } from '../elements/QuickTree/TerceraQuickTree';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { DynProperty } from '@shared/commons/DynProperty';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import AssetsPanelTemplate from '@front/templates/AssetsPanelTemplate.html';
import { LinkedSystem } from '../misc/LinkedSystem';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { NewContextMenuItemType, createTagDictionary } from '@shared/utils/ContextMenuUtils';
export class AssetsPanel extends ApplicationPanelWithTable {
    constructor() {
        super(...arguments);
        this.Name = 'AssetsPanel';
        this.myAccount = null;
        this.accLookup = null;
        this.headerLocaleKey = 'panel.assets';
        this.ToolbarVisible = true;
        this.InitShowTotals = false;
        this.staticItems = [];
        this.noDataLabel = null;
    }
    getType() {
        return PanelNames.AssetsPanel;
    }
    oncomplete() {
        super.oncomplete();
        const menuItems = this.createMenuItems();
        this.staticItems = menuItems;
        this.menuTagDict = createTagDictionary(menuItems);
        this.getQuickTable().setTableContextMenuItems(menuItems);
        this.selectionChange();
        this.UpdateShowTotalsStateChange(!!this.InitShowTotals);
        this.ShowToolbarStateUpdate(!!this.ToolbarVisible);
        if (!isNullOrUndefined(this.menuTagDict)) {
            if (!isNullOrUndefined(this.menuTagDict.ShowTotals)) {
                this.menuTagDict.ShowTotals.checked = !!this.InitShowTotals;
            }
            if (!isNullOrUndefined(this.menuTagDict.ToolbarVisible)) {
                this.menuTagDict.ToolbarVisible.checked = !!this.ToolbarVisible;
            }
        }
        this.layoutTable();
        this.localize();
    }
    getAssetsAccountsLookup() {
        return this.Controls.accountSelector.getLookup();
    }
    jbInit() {
        super.jbInit();
        const noDataLabel = this.Controls.noDataLabel;
        this.noDataLabel = noDataLabel;
        const accountsLookup = this.getAssetsAccountsLookup();
        accountsLookup.onChangeValue.Subscribe(this.accountsLookupOnChangeValue, this);
        accountsLookup.IsMultiSelect = true;
        accountsLookup.isMultiSelectMode = true;
        this.accLookup = accountsLookup;
        if (isValidString(this.delayAccounts)) {
            const delayAccountsArrayString = this.delayAccounts.split(',');
            const delayAccountsArrayAccount = [];
            const len = delayAccountsArrayString.length;
            for (let i = 0; i < len; i++) {
                if (!isNullOrUndefined(DataCache.Accounts[delayAccountsArrayString[i]]) ||
                    delayAccountsArrayString[i] === 'All') {
                    delayAccountsArrayAccount.push(DataCache.Accounts[delayAccountsArrayString[i]]);
                }
            }
            accountsLookup.dropDownFormCallBack(delayAccountsArrayAccount);
            delete this.delayAccounts;
        }
        else {
            let accToInitWith = accountsLookup.ItemAll;
            if (DataCache.EnableForceLinkingByAccount()) {
                const accID = LinkedSystem.getAccount();
                accToInitWith = [DataCache.GetAccountById(accID)];
            }
            accountsLookup.dropDownFormCallBack(accToInitWith);
        }
        this.getQuickTable().OnSelectionChanged.Subscribe(this.selectionChange, this);
        this.getQuickTable().OnPaintedPictureButtonClick.Subscribe(this.onPaintedPictureButtonClick, this);
        this.getQuickTable().AfterEditItem.Subscribe(this.onAfterEditItem, this);
        this.quickTableRactive.on(TerceraQuickTreeEvents.DoubleClick, this.onDoubleClicked.bind(this));
        DataCache.OnUpdateAccount.Subscribe(this.UpdateAccount, this);
    }
    UpdateAccount(account) {
        this.FillRowsByAcc(account);
        const len = this.getQuickTable().rowsArray.length;
        if (!isNullOrUndefined(this.noDataLabel)) {
            void this.set('noDataLabelVisible', !isValidNumber(len) || len === 0);
        }
    }
    FillRowsByAcc(account) {
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt) || isNullOrUndefined(account)) {
            return;
        }
        const sharesNames = account.AssetSharesNames;
        const processingRows = [];
        for (let i = 0; i < qt.rowsArray.length; i++) {
            const row = qt.rowsArray[i];
            const item = row.item;
            if (isNullOrUndefined(item)) {
                continue;
            }
            const secondQuantor = !isValidArray(sharesNames) || !sharesNames.includes(item.Asset.Name);
            if (item.Account === account) {
                if (secondQuantor) {
                    qt.RemoveItem(item.ItemId);
                }
                else {
                    processingRows.push(row);
                }
            }
        }
        if (isValidArray(sharesNames)) {
            for (let i = 0; i < sharesNames.length; i++) {
                const name = sharesNames[i];
                const assetData = account.GetAssetDataInfoByName(name);
                if (assetData == null) {
                    continue;
                }
                const asset = DataCache.GetAssetByName(name);
                if (asset == null) {
                    continue;
                }
                let found = false;
                for (let i = 0; i < processingRows.length; i++) {
                    const row = processingRows[i];
                    const item = row.item;
                    if (isNullOrUndefined(item)) {
                        continue;
                    }
                    if (item.Account === account && item.Asset === asset) {
                        item.UpdatInfoData();
                        item.fireUpdate();
                        found = true;
                        processingRows.splice(i, 1);
                        break;
                    }
                }
                if (!found) {
                    const row = this.getQuickTable().AddItem(new AssetsItem(account, asset, SessionSettings));
                    this.ProcessEditingItem(row);
                }
            }
        }
    }
    ProcessEditingItem(row) {
        const lotValueCell = row.cells[AssetsItem.COL_SELL_EXCHANGE];
        if (isNullOrUndefined(lotValueCell.QuickTableEditingInfo)) {
            const info = new QuickTableEditingInfo(DynProperty.COMBOBOX);
            info.GetDataHandler = this.GetCBDataForRow;
            lotValueCell.QuickTableEditingInfo = info;
            lotValueCell.ReadOnly = false;
        }
    }
    GetCBDataForRow(row) {
        const comboboxItems = [];
        if (!row.item.SellExchanges) {
            return comboboxItems;
        }
        for (let i = 0; i < row.item.SellExchanges.length; i++) {
            comboboxItems.push({
                type: NewContextMenuItemType.Default,
                text: row.item.SellExchanges[i],
                value: row.item.SellExchanges[i]
            });
        }
        return comboboxItems;
    }
    onAfterEditItem(params) {
        const row = params.row;
        if (isNullOrUndefined(row))
            return;
        row.item.SetSellExchange(params.newValue);
    }
    onPaintedPictureButtonClick(args, event) {
        var _a;
        if (isNullOrUndefined((_a = args === null || args === void 0 ? void 0 : args.row) === null || _a === void 0 ? void 0 : _a.item)) {
            return;
        }
        this.OpenOE(args.row.item, args.row.item.GetSellExchange());
    }
    OpenOE(item, preferExchange) {
        if (isNullOrUndefined(item === null || item === void 0 ? void 0 : item.Asset)) {
            return;
        }
        let instrument = null;
        const asset = item.Asset;
        const exchange = preferExchange;
        instrument = asset.Instrument;
        let insPromise = null;
        if ((exchange && !isNullOrUndefined(instrument) && instrument.TradingExchange !== exchange) ||
            isNullOrUndefined(instrument)) {
            const exch = exchange || item.GetSellExchange();
            const InstrumentInterriorId = asset.GetPreferedInstrumentInterriorIdByExchange(exch);
            if (!isNullOrUndefined(InstrumentInterriorId)) {
                const sp = InstrumentInterriorId.split(InstrumentUtils.SEPARATOR);
                insPromise = DataCache.getInstrumentByInstrumentTradableID_NFL(Number.parseInt(sp[0]), Number.parseInt(sp[1]), InstrumentInterriorId);
            }
            else {
                insPromise = (function () {
                    return __awaiter(this, void 0, void 0, function* () {
                        return yield Promise.resolve(DataCache.FindInstrumentByTradingExchange(exch));
                    });
                })();
            }
        }
        else {
            insPromise = (function () {
                return __awaiter(this, void 0, void 0, function* () {
                    return yield Promise.resolve(instrument);
                });
            })();
        }
        if (isNullOrUndefined(insPromise)) {
            return;
        }
        insPromise.then(function (instrument) {
            if (isNullOrUndefined(instrument)) {
                return;
            }
            const acc = item.Account;
            const assetInfo = acc.GetAssetDataInfoByName(asset.Name);
            const lotSize = instrument.LotSize;
            const productType = ProductType.Delivery; // always Delivery?
            // Open OE.
            const openOEMethod = () => {
                MainWindowManager.Factory.addPanel(PanelNames.AdvancedOrderEntry, null, (panel) => {
                    // panel.symbolLink_In(instrument.GetInteriorID());
                    const inLots = WDSettingsUtils.displayAmountInLots();
                    const convertedValue = Quantity.convertQuantityValue(new Quantity(assetInfo.GetAvailableQty() / lotSize, true), instrument, inLots, acc, productType);
                    const quantity = new Quantity(convertedValue, inLots);
                    panel.set({ instrument }).then(() => {
                        panel.set({
                            side: OperationType.Sell,
                            account: acc,
                            isCashQtyMode: false, // #124187
                            defaultQuantity: quantity
                        });
                        if (instrument.isProductTypeVisible()) {
                            panel.set({ productType });
                        }
                        panel.setDisclosedQuantityParams('quantity');
                    });
                });
            };
            // setFocus Hack
            setTimeout(openOEMethod, 0);
        });
    }
    onDoubleClicked() {
        const qt = this.getQuickTable();
        const selectedRowIds = qt.selectedRowIds;
        if (isValidArray(selectedRowIds)) {
            const row = qt.rows[selectedRowIds[0]];
            if (!isNullOrUndefined(row)) {
                this.OpenOE(row.item);
            }
        }
    }
    accountsLookupOnChangeValue(accountsResult) {
        if (isNullOrUndefined(this.getQuickTable())) {
            return;
        }
        this.getQuickTable().ClearRows();
        if (accountsResult instanceof _AccountItemAll) {
            const accounts = DataCache.getAccounts();
            const keys = Object.keys(accounts);
            for (let i = 0; i < keys.length; i++) {
                this.FillRowsByAcc(accounts[keys[i]]);
            }
        }
        else if (accountsResult instanceof _AccountItemSelected) {
            for (let i = 0; i < accountsResult.arraySelected.length; i++) {
                this.FillRowsByAcc(accountsResult.arraySelected[i]);
            }
        }
        else {
            this.FillRowsByAcc(accountsResult);
        }
        const len = this.getQuickTable().rowsArray.length;
        if (!isNullOrUndefined(this.noDataLabel)) {
            void this.set('noDataLabelVisible', !isValidNumber(len) || len === 0);
        }
    }
    createMenuItems() {
        const items = [];
        this.AddExportContextMenuItem(items);
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'screen.reports.print',
            event: this.printTable.bind(this),
            visible: !Resources.isHidden('panel.trades.print')
        });
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'panel.positions.menu.View',
            svgImage: 'View',
            subitems: [
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.Assets.Menu.ShowToolbar',
                    canCheck: true,
                    checked: this.ToolbarVisible,
                    visible: DataCache.getNumberOfAccounts() !== 1 && !Resources.isHidden('panel.Assets.Menu.ShowToolbar'),
                    event: this.ShowToolbarStateChange.bind(this)
                },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.positions.menu.ShowTotals',
                    checked: false,
                    canCheck: true,
                    event: this.ShowTotalsStateChange.bind(this)
                }
            ]
        });
        return items;
    }
    ShowToolbarStateChange(menuItem) {
        this.ShowToolbarStateUpdate(menuItem.checked);
    }
    ShowToolbarStateUpdate(state) {
        if (DataCache.getNumberOfAccounts() === 1) {
            state = false;
        }
        void this.accLookup.set('visible', state);
        this.layoutTable();
    }
    localize() {
        super.localize();
        void this.set({
            noAssetsAvailableText: Resources.getResource('panel.Assets.NoAssetsAvailable')
        });
    }
    SetColumnsDefaultDisplayIndex(table) {
        table.columns[0].displayedIndex = 1;
        table.columns[1].displayedIndex = 2;
        table.columns[2].displayedIndex = 3;
        table.columns[3].displayedIndex = 4;
        table.columns[4].displayedIndex = 5;
        table.columns[5].displayedIndex = 6;
        table.columns[6].displayedIndex = 7;
        table.columns[7].displayedIndex = 8;
        table.columns[8].displayedIndex = 9;
        table.columns[9].displayedIndex = 10;
        table.columns[10].displayedIndex = 11;
        table.columns[11].displayedIndex = 15;
        table.columns[14].displayedIndex = 14;
    }
    selectionChange() {
        const qt = this.getQuickTable();
        const selectedRowIds = qt.selectedRowIds;
        if (!isValidArray(selectedRowIds)) {
            return;
        }
        const items = [];
        if (selectedRowIds.length === 0) {
            items.push({
                type: NewContextMenuItemType.Default,
                visible: selectedRowIds.length === 0,
                text: 'panel.Assets.Menu.SellAsset',
                tooltip: 'IsAllowedResponceReason.NoAssetsSelected',
                enabled: false,
                svgImage: 'menuError',
                event: function () { }
            });
        }
        else if (selectedRowIds.length > 1) {
            items.push({
                type: NewContextMenuItemType.Default,
                visible: selectedRowIds.length > 1,
                text: 'panel.Assets.Menu.SellAsset',
                tooltip: 'IsAllowedResponceReason.MoreThanOneAssetIsSelected',
                enabled: false,
                svgImage: 'menuError',
                event: function () { }
            });
        }
        else {
            const row = qt.rows[selectedRowIds[0]];
            if (!isNullOrUndefined(row)) {
                const exchArr = this.GetCBDataForRow(row);
                for (let i = 0; i < exchArr.length; i++) {
                    items.push({
                        type: NewContextMenuItemType.Default,
                        text: 'panel.Assets.Menu.SellAssetOn',
                        additionalText: exchArr[i].text,
                        event: this.OpenOE.bind(this, row.item, exchArr[i].text)
                    });
                }
            }
        }
        const menuItems = items.concat(this.staticItems);
        this.menuTagDict = createTagDictionary(menuItems);
        qt.setTableContextMenuItems(menuItems);
    }
    Properties() {
        const properties = super.Properties();
        const acc = this.accLookup.get('selectedItem');
        if (isNullOrUndefined(acc)) {
            return properties;
        }
        let accNumber = '';
        if (acc instanceof _AccountItemAll || acc instanceof _AccountItemSelected) {
            accNumber = acc.GetDataForSave();
        }
        else {
            accNumber = acc.AcctNumber;
        }
        properties.push(new DynProperty('account', accNumber, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('ShowTotals', this.getQuickTable().ShowTotals, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('ToolbarVisible', this.ToolbarVisible, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        return properties;
    }
    callBack(newProperties) {
        super.callBack(newProperties);
        const dp = DynProperty.getPropertyByName(newProperties, 'account');
        if (isValidString(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this.delayAccounts = dp.value;
        }
        this.InitShowTotals = DynProperty.getPropValue(newProperties, 'ShowTotals');
        this.ToolbarVisible = DynProperty.getPropValue(newProperties, 'ToolbarVisible');
    }
    repopulate() {
        super.repopulate();
        if (!isNullOrUndefined(this.accLookup)) {
            this.accountsLookupOnChangeValue(this.accLookup.get('selectedItem'));
        }
    }
    dispose() {
        DataCache.OnUpdateAccount.UnSubscribe(this.OnUpdateAccount, this);
        super.dispose();
    }
    accountLink_In(accId, fromLinkedSystem = false) {
        super.accountLink_In(accId, fromLinkedSystem);
        if (DataCache.EnableForceLinkingByAccount()) {
            const acc = DataCache.GetAccountById(accId);
            const accountsLookup = this.getAssetsAccountsLookup();
            accountsLookup === null || accountsLookup === void 0 ? void 0 : accountsLookup.dropDownFormCallBack([acc]);
            this.UpdateAccount(acc);
        }
        return true;
    }
}
ApplicationPanelWithTable.extendWith(AssetsPanel, {
    Name: 'AssetsPanel',
    data: function () {
        return {
            isAccountLinkShow: true,
            isSymbolLinkShow: true,
            noDataLabelVisible: false
        };
    },
    partials: {
        bodyPartial: AssetsPanelTemplate
    },
    myAccount: null,
    accLookup: null,
    headerLocaleKey: 'panel.assets',
    ToolbarVisible: true,
    InitShowTotals: false,
    noDataLabel: null
});
